import { type EvalTaskSpec, EvalTaskSpecSchema, type Grader } from '@sbc/domain-models/agents';
import { parse, stringify, YAMLParseError } from 'yaml';
import { z } from 'zod';

export type ParseTaskYamlResult = { ok: true; spec: EvalTaskSpec } | { ok: false; error: string };

export function parseTaskYaml(text: string): ParseTaskYamlResult {
  let raw: unknown;
  try {
    raw = parse(text);
  } catch (caught) {
    return {
      ok: false,
      error: caught instanceof YAMLParseError ? caught.message : 'Invalid YAML.',
    };
  }
  const result = EvalTaskSpecSchema.safeParse(raw);
  if (!result.success) {
    return { ok: false, error: z.prettifyError(result.error) };
  }
  return { ok: true, spec: result.data };
}

export function taskSpecToYaml(spec: EvalTaskSpec): string {
  // Emit only the spec fields, dropping unset optionals, in a stable, readable order.
  const ordered: Record<string, unknown> = {
    taskKey: spec.taskKey,
    suite: spec.suite,
    input: spec.input,
    ...(spec.expectTools ? { expectTools: spec.expectTools } : {}),
    ...(spec.expectNoTools ? { expectNoTools: spec.expectNoTools } : {}),
    ...(spec.expectAnswer ? { expectAnswer: spec.expectAnswer } : {}),
    grade: spec.grade,
    trials: spec.trials,
    passThreshold: spec.passThreshold,
    ...(spec.replay ? { replay: spec.replay } : {}),
  };
  return stringify(ordered);
}

function graderLabel(grader: Grader): string {
  if (grader === 'tool-selection') {
    return 'tool selection';
  }
  if (grader === 'answer-contains') {
    return 'answer contains';
  }
  if (grader === 'draft-vs-cs') {
    return 'draft vs CS reply (shadow grader)';
  }
  if ('toolJson' in grader) {
    return `tool JSON (${grader.toolJson.tool} ${grader.toolJson.path})`;
  }
  const firstLine = grader.judge.rubric.split('\n')[0]?.trim() ?? '';
  const preview = firstLine.length > 60 ? `${firstLine.slice(0, 60)}…` : firstLine;
  return `LLM judge (rubric: ${preview})`;
}

export function describeTask(spec: EvalTaskSpec): string[] {
  const lines: string[] = [`Tag: ${spec.suite}`, `Sends the input: "${spec.input}"`];
  if (spec.replay) {
    lines.push(
      `Replays Intercom conversation ${spec.replay.conversationId} (${spec.replay.category}) with ${spec.replay.recorded.length} recorded tool results.`,
    );
  }
  if (spec.expectNoTools) {
    lines.push('Expects the agent to call no tools.');
  } else if (spec.expectTools && spec.expectTools.length > 0) {
    lines.push(`Expects the agent to call: ${spec.expectTools.join(', ')}.`);
  } else {
    lines.push('No tool expectation.');
  }
  if (spec.expectAnswer) {
    lines.push(`Answer must contain: "${spec.expectAnswer}".`);
  }
  lines.push(`Graded by: ${spec.grade.map(graderLabel).join('; ')}.`);
  lines.push(
    spec.trials > 1
      ? `Runs up to ${spec.trials} trials; passes when ≥ ${spec.passThreshold} pass.`
      : 'Runs once.',
  );
  return lines;
}

// A valid starter task so the live explanation shows a real summary (not an error)
// the moment the New task page opens.
export const TASK_TEMPLATE = `taskKey: my-task-key
suite: capability            # capability | regression
input: What is double cleansing and why do people do it?
expectTools: [kb-read]
# expectNoTools: true        # use instead of expectTools for should-not-call cases
# expectAnswer: double cleanse   # required if you add the answer-contains grader
grade:
  - tool-selection
  # - answer-contains
  # - judge:
  #     rubric: |
  #       Pass only if the answer explains ...
  #     reference: |
  #       (optional) facts the judge may rely on
trials: 1
passThreshold: 1
`;
